import { Chart } from "@appretio/ui-kit";
import type { AgChartOptions } from "ag-charts-enterprise";
import { useMemo } from "react";

/** Datum shape consumed by the waterfall chart. */
interface WaterfallDatum {
  x: string;
  y: number;
  isTotal: boolean;
}

/**
 * Shape of the waterfall series config we build.
 *
 * ``ag-charts-enterprise`` registers the ``"waterfall"`` series at
 * runtime via its module system, but the public type exports on
 * ``AgChartOptions["series"]`` are limited to the community-tier series
 * (bar, line, area, pie, ...). Rather than silence the resulting type
 * error with ``as never``, we document the shape we pass here and cast
 * once at the boundary where the whole options object meets
 * ``AgChartOptions``. Using a typed local interface keeps the config
 * body type-checked — only the boundary cast is unchecked.
 */
interface WaterfallSeriesConfig {
  type: "waterfall";
  xKey: "x";
  yKey: "y";
  totals: {
    totalType: "subtotal" | "total";
    index: number;
    axisLabel: string;
  }[];
  item: {
    positive: {
      fill: string;
      stroke: string;
      itemStyler: (params: { datum: WaterfallDatum }) => {
        fill?: string;
        stroke?: string;
      };
    };
    negative: { fill: string; stroke: string };
    total: { fill: string; stroke: string };
  };
  label: {
    enabled: boolean;
    fontSize: number;
    formatter: (params: { value: number }) => string;
  };
}

export interface WaterfallGraphProps {
  /** Columnar grid data (metric|agg → [tables][rows]). */
  data: Record<string, (number | null)[][]>;
  /** Row labels for the selected table. */
  rowLabels: string[];
  /** Selected metric to display (e.g. "GS1"). */
  metric: string;
  /** Display name for the metric (e.g. "GS I"). */
  metricLabel: string;
  /** Selected table index. */
  tableIndex: number;
  /** Selected table label (e.g. "non-noos"). */
  tableLabel: string;
  /** Selected aggregation (PTD/PTG/TTL). For ALL mode, uses TTL. */
  agg: string;
  /** Row dimension label (e.g. "brand"). */
  rowDim: string;
  /** Label for the starting bar (index source). */
  indexLabel: string;
  /** Label for the ending bar (main source). */
  mainLabel: string;
  /** Index column suffix. */
  indexSuffix?: string;
}

const TOTAL_COLOR = "hsla(52, 100%, 36%, 1)";
const POSITIVE_COLOR = "hsla(182, 100%, 38%, 1)";
const NEGATIVE_COLOR = "hsla(4, 91%, 70%, 1)";

/**
 * Waterfall chart comparing main vs index dataset for a selected metric.
 *
 * - First bar (absolute): index total (gold via itemStyler)
 * - Middle bars (relative): delta per row dimension value
 * - Last bar (total): main total (gold via totals config)
 */
export function WaterfallGraph({
  data,
  rowLabels,
  metric,
  metricLabel,
  tableIndex,
  tableLabel,
  agg,
  rowDim,
  indexLabel,
  mainLabel,
  indexSuffix = "|index"
}: WaterfallGraphProps) {
  const options = useMemo((): AgChartOptions => {
    const effectiveAgg = agg === "ALL" ? "TTL" : agg;
    const mainCol = `${metric}|${effectiveAgg}`;
    const indexCol = `${metric}|${effectiveAgg}${indexSuffix}`;

    const mainValues = data[mainCol]?.[tableIndex] ?? [];
    const indexValues = data[indexCol]?.[tableIndex] ?? [];

    // Find TTL row index.
    const ttlIdx = rowLabels.indexOf("TTL");

    // Build waterfall data: index TTL as first value, then per-row deltas.
    const chartData: WaterfallDatum[] = [];

    // Starting bar: index TTL value (from 0, colored gold via itemStyler).
    const indexTotal = ttlIdx !== -1 ? (indexValues[ttlIdx] ?? 0) : 0;
    chartData.push({ x: indexLabel, y: indexTotal, isTotal: true });

    // Middle bars: delta per non-TTL row (relative).
    for (let ri = 0; ri < rowLabels.length; ri++) {
      if (rowLabels[ri] === "TTL") continue;
      const mainVal = mainValues[ri] ?? 0;
      const indexVal = indexValues[ri] ?? 0;
      const delta = mainVal - indexVal;
      chartData.push({ x: rowLabels[ri], y: delta, isTotal: false });
    }

    // Placeholder for the ending total — the subtotal replaces it visually.
    chartData.push({ x: mainLabel, y: 0, isTotal: false });

    const waterfallSeries: WaterfallSeriesConfig = {
      type: "waterfall",
      xKey: "x",
      yKey: "y",
      totals: [
        {
          totalType: "subtotal",
          index: chartData.length - 1,
          axisLabel: mainLabel
        }
      ],
      item: {
        positive: {
          fill: POSITIVE_COLOR,
          stroke: POSITIVE_COLOR,
          itemStyler: ({ datum }) =>
            datum.isTotal ? { fill: TOTAL_COLOR, stroke: TOTAL_COLOR } : {}
        },
        negative: { fill: NEGATIVE_COLOR, stroke: NEGATIVE_COLOR },
        total: { fill: TOTAL_COLOR, stroke: TOTAL_COLOR }
      },
      label: {
        enabled: true,
        fontSize: 11,
        formatter: ({ value }) =>
          value != null
            ? value.toLocaleString("en-US", { maximumFractionDigits: 0 })
            : ""
      }
    };

    return {
      title: {
        text: `${metricLabel} — ${tableLabel} (by ${rowDim})`,
        fontSize: 14
      },
      height: 500,
      padding: { top: 10, right: 20, bottom: 10, left: 20 },
      legend: { enabled: false },
      data: chartData,
      series: [waterfallSeries],
      axes: [
        {
          type: "category",
          position: "bottom",
          label: { rotation: 45, fontSize: 11 }
        },
        {
          type: "number",
          position: "left",
          label: { fontSize: 11 }
        }
      ]
      // AgChartOptions["series"] is a union of the community-tier series
      // shapes; ``"waterfall"`` is registered at runtime by the
      // enterprise module but not present in the static type union.
      // See the ``WaterfallSeriesConfig`` comment above.
    } as unknown as AgChartOptions;
  }, [
    data,
    rowLabels,
    metric,
    metricLabel,
    tableIndex,
    tableLabel,
    agg,
    rowDim,
    indexLabel,
    mainLabel,
    indexSuffix
  ]);

  return <Chart options={options} isLoading={false} />;
}
